/**
 * One column of a CSV export: the heading a spreadsheet shows, and the value each row contributes. A `null` value is written as an empty cell.
 */
export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | null;
}

/** A spreadsheet treats a text cell that starts with one of these as a formula, so such text is written with a leading apostrophe. */
const formulaStart = /^[=+\-@\t\r]/;

/**
 * Writes one cell of a CSV file: text that starts like a formula is neutralised, and a cell with a comma, a quote or a line break is quoted.
 *
 * @param value - The cell's value.
 * @returns The cell as it is written in the file.
 */
export function csvCell(value: string | number | null): string {
  if (value === null) {
    return "";
  }

  const text = typeof value === "string" && formulaStart.test(value) ? `'${value}` : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

/**
 * Writes rows as a CSV file: a heading line, then one line per row, in the order given.
 *
 * @param rows - The rows to write, all of them.
 * @param columns - The columns, in the order they are written.
 * @returns The file's text, with CRLF line endings.
 */
export function toCsv<T>(rows: readonly T[], columns: readonly CsvColumn<T>[]): string {
  const lines = [columns.map((column) => csvCell(column.header)), ...rows.map((row) => columns.map((column) => csvCell(column.value(row))))];
  return `${lines.map((cells) => cells.join(",")).join("\r\n")}\r\n`;
}

/**
 * Saves text as a file the browser downloads. It is created and clicked in the same handler, so the download is part of the user's action.
 *
 * @param fileName - The name the file is saved under.
 * @param text - The file's text.
 */
export function downloadCsv(fileName: string, text: string): void {
  // A byte order mark lets a spreadsheet read the file as UTF-8, so names with accents survive.
  const url = URL.createObjectURL(new Blob(["﻿", text], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
